import React from "react";
import { Link } from "react-router-dom";
import "./EnergyUtilities.css";
import { navigateToHomeSection } from '../utils/navigation';

const navLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];
const footerLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];

const metaCards = [
  { label: "SECTOR", value: "Energy & Utilities" },
  { label: "FOCUS", value: "Risk · Ops · Customer" },
  { label: "MATURITY", value: "Production-grade" },
  { label: "COVERAGE", value: "EU & UK" },
];

const plays = [
  { id: "01", title: "Predictive Maintenance", desc: "ML on sensor and SCADA data to predict and prevent asset failures." },
  { id: "02", title: "Demand & Generation Forecasting", desc: "Short and medium-term forecasting for trading and operations." },
  { id: "03", title: "Grid & Network Optimisation", desc: "Optimisation for constraint management and DERMS." },
  { id: "04", title: "Customer & Vulnerability Analytics", desc: "Better service for vulnerable customers; smarter retention." },
  { id: "05", title: "Asset Management Platforms", desc: "Unified data platforms across OT, IT and ERP." },
  { id: "06", title: "Sustainability Analytics", desc: "Carbon and ESG analytics aligned to disclosure standards" },
];

const phases = [
  { phase: "PHASE 01", title: "Discover", desc: "Quantify the addressable opportunity across asset and customer domains.", align: "left" },
  { phase: "PHASE 02", title: "Pilot", desc: "Prove value on a single asset class or geography.", align: "right" },
  { phase: "PHASE 03", title: "Industrialise", desc: "Productionise with OT-grade reliability and cyber controls.", align: "left" },
  { phase: "PHASE 04", title: "Scale", desc: "Roll out across the asset estate and into operating models.", align: "right" },
];

const outcomes = [
  { num: "/ 01", text: "Reduction in unplanned downtime on critical assets" },
  { num: "/ 02", text: "Better forecasting that supports trading and operations" },
  { num: "/ 03", text: "Lower OPEX through condition-based maintenance" },
  { num: "/ 04", text: "A data foundation that supports the energy transition" },
];

const otherSectors = [
  { name: "Financial Services", desc: "AI for banking, lending and insurance", img: "financial" ,link: "/financial-services"},
  { name: "Education", desc: "AI for higher education and learning", img: "education" ,link: "/education"},
  { name: "Healthcare", desc: "Trustworthy AI for clinical and operational excellence", img: "health" ,link: "/healthcare"},
];

export default function EnergyUtilities() {
  return (
    <div className="eng-root">

      {/* ── HERO  ── */}
      <section className="eng-hero">
        <nav className="eng-nav">
          <div className="eng-nav-inner">
            <div className="eng-logo">
              <span className="eng-logo-aiv">AIV</span><span className="eng-logo-ion">ION</span>
            </div>
              <ul className="eng-nav-links">
                {navLinks.map((l) => <li key={l.label}><a href={l.href} onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a></li>)}
              </ul>
            <a href="/#contact" className="eng-contact-btn" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>CONTACT US</a>
          </div>
        </nav>

        <div className="eng-hero-content">
          <p className="eng-hero-breadcrumb">&#8592; INDUSTRIES</p>
          <div className="eng-hero-sector">
            <span className="eng-sector-icon">&#127974;</span>
            <span>SECTOR FOCUS</span>
          </div>
          <h1 className="eng-hero-title">Energy & Utilities</h1>
          <p className="eng-hero-sub">AI for the energy transition</p>
        </div>
      </section>

      {/* ── META CARD ROW (overlapping hero/white) ── */}
      <div className="eng-meta-wrap">
        <div className="eng-meta-row">
          {metaCards.map((m) => (
            <div key={m.label} className="eng-meta-card">
              <p className="eng-meta-label">{m.label}</p>
              <p className="eng-meta-value">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── OPPORTUNITY SECTION ── */}
      <section className="eng-opportunity">
        <div className="eng-opportunity-inner">
          <div className="eng-opp-left">
            <p className="eng-eyebrow">THE OPPORTUNITY</p>
            <h2 className="eng-opp-title">Where AI is changing Energy & Utilities.</h2>
            <span className="eng-opp-pill">&#9679; AIVION sector practice</span>
          </div>
          <div className="eng-opp-right">
            <p className="eng-dropcap-para">
              <span className="eng-dropcap">A</span>IVION helps generators, transmission and distribution operators, retailers and water utilities apply AI to the dual challenge of decarbonisation and resilience. From predictive maintenance on critical assets to short-term demand forecasting and DERMS optimisation, we deliver solutions that combine industrial OT data with modern cloud analytics — and that operate safely in mission-critical environments.
            </p>
            <blockquote className="eng-quote">
              <span className="eng-quote-mark">&#8220;</span>
              <p>Trustworthy AI for clinical and operational excellence</p>
              <cite>AIVION — Energy & Utilities PRACTICE</cite>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ── SIX HIGH-IMPACT PLAYS ── */}
      <section className="eng-plays">
        <div className="eng-plays-inner">
          <div className="eng-plays-header">
            <div>
              <p className="eng-eyebrow">WHERE WE WORK</p>
              <h2 className="eng-plays-title">Six high-impact plays</h2>
            </div>
            <span className="eng-plays-count">06 AREAS</span>
          </div>
          <div className="eng-plays-list">
            {plays.map((p) => (
              <div key={p.id} className="eng-play-row">
                <span className="eng-play-num">{p.id}</span>
                <h4 className="eng-play-title">{p.title}</h4>
                <p className="eng-play-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENGAGEMENT MODEL — ZIGZAG PHASES ── */}
      <section className="eng-phases">
        <div className="eng-phases-inner">
          <p className="eng-eyebrow eng-center">ENGAGEMENT MODEL</p>
          <h2 className="eng-phases-title eng-center">From assessment to lasting capability</h2>
          <div className="eng-phases-track">
            {phases.map((ph) => (
              <div key={ph.phase} className={`eng-phase-row eng-phase-${ph.align}`}>
                <div className="eng-phase-card">
                  <p className="eng-phase-label">{ph.phase}</p>
                  <h4 className="eng-phase-name">{ph.title}</h4>
                  <p className="eng-phase-desc">{ph.desc}</p>
                </div>
                <span className="eng-phase-dot" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUTCOMES (dark) ── */}
      <section className="eng-outcomes">
        <div className="eng-outcomes-inner">
          <p className="eng-eyebrow">THE IMPACT</p>
          <h2 className="eng-outcomes-title">Outcomes our Energy & Utilities clients see.</h2>
          <div className="eng-outcomes-grid">
            {outcomes.map((o) => (
              <div key={o.num} className="eng-outcome-cell">
                <span className="eng-outcome-num">{o.num}</span>
                <p>{o.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OTHER SECTORS ── */}
      <section className="eng-other-sectors">
        <div className="eng-other-inner">
          <div className="eng-other-header">
            <div>
              <p className="eng-eyebrow">OTHER SECTORS</p>
              <h2 className="eng-other-title">Across the industries we serve</h2>
            </div>
            <Link to="/industries" className="eng-other-all">All industries &#8594;</Link>
          </div>
          <div className="eng-other-grid">
            {otherSectors.map((s) => (
              <div key={s.name} className={`eng-sector-card eng-img-${s.img}`}>
                <div className="eng-sector-img" />
                <div className="eng-sector-info">
                  <p className="eng-sector-tag">INDUSTRY</p>
                  <h4>{s.name}</h4>
                  <p className="eng-sector-desc">{s.desc}</p>
                  <Link to={s.link} className="eng-sector-explore">EXPLORE &#8594;</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER (dark) ── */}
      <section className="eng-cta">
        <div className="eng-cta-inner">
          <div className="eng-cta-left">
            <p className="eng-eyebrow">START THE CONVERSATION</p>
            <h2 className="eng-cta-title">Bring AIVION's Energy & Utilities expertise to your team.</h2>
            <p className="eng-cta-desc">
              A focused 30-minute briefing with one of our sector leads — exploring the highest-value AI plays for
              your business and how to get there safely.
            </p>
          </div>
          <div className="eng-cta-right">
            <a href="/#contact" className="eng-cta-btn-solid" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>REQUEST A SECTOR BRIEFING &#8594;</a>
            <button className="eng-cta-btn-outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>SEE SECTOR CASE STUDIES</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="eng-footer">
        <div className="eng-footer-inner">
          <div className="eng-footer-brand">
            <div className="eng-footer-logo">
              <span className="eng-logo-aiv">AIV</span><span className="eng-logo-ion">ION</span>
            </div>
            <p>Delivering meaningful outcomes through intelligent AI solutions.</p>
            <div className="eng-footer-icons">
              <span className="eng-icon">in</span>
              <span className="eng-icon">&#9993;</span>
            </div>
          </div>
          <div className="eng-footer-col">
            <p className="eng-footer-heading">QUICK LINKS</p>
            <div className="footer-quick-links-grid">
              {footerLinks.map((l) => <a key={l.label} href={l.href} className="eng-footer-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a>)}
            </div>
          </div>
        </div>
        <div className="eng-footer-bottom">
          <p>&#169; 2026 AIVION. All rights reserved.</p>
          
        </div>
      </footer>

    </div>
  );
}
